import * as React from 'react';
import { useEffect, useState } from 'react';
import { OrganizationOwner, PublicAmorgUser, rng, WorkgroupListing } from '../../amorg-common';
import { getOwner } from '../../api/organizationClientApi';
import { getUsers } from '../../api/userClientApi';
import { createWorkgroup, getWorkgroups } from '../../api/workgroupClientApi';
import { Modal } from '../../designSystem/Modal';
import { PageHeader } from '../../designSystem/PageHeader';
import { Button } from '../../designSystem/button/Button';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { P } from '../../designSystem/typography/Body';
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';
import { OrgChart } from './_components/OrgChart';
import OrgOwnerEditor from './_components/OrgOwnerEditor';
import { WorkgroupEditor } from './_components/WorkgroupEditor';

type WorkgroupListPageState = {
    allUsers: PublicAmorgUser[];
    workgroupList: WorkgroupListing[];
    inEditWorkgroup: WorkgroupListing | null;

    organizationOwner: OrganizationOwner | null;
    inEditOrganizationOwner: OrganizationOwner | null;
}
export function OrganizationPage(): JSX.Element {
    return (
        <MemberPageWrapper selectedMenuItemKey='workgroups' breadCrumbs={[{ text: 'Organisation', url: '' }]}>
            <OrganizationPageContent />
        </MemberPageWrapper>
    );
}

function OrganizationPageContent(): JSX.Element {

    const [state, setState] = useState<WorkgroupListPageState>(
        {
            workgroupList: [],
            allUsers: [],
            inEditWorkgroup: null,
            organizationOwner: null,
            inEditOrganizationOwner: null
        });

    const [editorOpen, setEditorOpen] = useState(false);

    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewOrganization']);

    useEffect(() => {
        const getData = async () => {
            const allUsers = await getUsers();
            const workgroupList = await getWorkgroups();
            const organizationOwner = await getOwner();
            setState(
                {
                    ...state,
                    allUsers,
                    workgroupList,
                    organizationOwner
                }
            );
        };
        getData();
    }, []);

    async function handleClickCreateNewWorkgroup() {

        const id = rng.getRandomId('wg_');

        const createResult = await createWorkgroup(id, 'Ny arbejdsgruppe');

        if (createResult.isSuccess) {

            setEditorOpen(true)

            const allWorkgroups = await getWorkgroups();
            const selectedWorkgroup = allWorkgroups.find(wg => wg.id === id) || null;
            setState(
                {
                    ...state,
                    workgroupList: allWorkgroups,
                    inEditWorkgroup: selectedWorkgroup
                });
        }
    }

    function handleClickEditWorkgroup(workgroupId: string) {

        setEditorOpen(true);
        setState(
            {
                ...state,
                inEditWorkgroup: state.workgroupList.find(wg => wg.id === workgroupId) || null
            });
    }

    function handleClickEditOwnerGroup() {

        if (state.organizationOwner === null) {
            setState({
                ...state,
                inEditOrganizationOwner: {
                    firstname: '', middlename: '', lastname: ''
                }
            })
        }
        else {
            setState({
                ...state,
                inEditOrganizationOwner: { ...state.organizationOwner }
            })
        }
    }

    async function handleWorkgroupUpdated(closeEditor: boolean) {
        const allUsers = await getUsers();
        const workgroupList = await getWorkgroups();
        const inEditWorkgroup = workgroupList.find(wg => wg.id === state.inEditWorkgroup?.id) || null;

        setState(
            {
                ...state,
                allUsers,
                workgroupList,
                inEditWorkgroup: closeEditor ? null : inEditWorkgroup,
            }
        );
    }

    async function handleOrganizationOwnerUpdated(closeEditor: boolean) {
        const organizationOwner = await getOwner();
        setState(
            {
                ...state,
                organizationOwner,
                inEditOrganizationOwner: closeEditor ? null : organizationOwner,
            }
        );
    }

    function handleWorkgroupEditorClose() {
        setState({ ...state, inEditWorkgroup: null })
    }

    function handleOrganizationOwnerEditorClose() {
        setState({ ...state, inEditOrganizationOwner: null });
    }

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    return (
        <>
            <PageHeader 
                narrow
                headerIcon='interface_user_team'
                headerText="Organisation" headerStyle="secondary-gray"
                buttons={
                    <Button variant="filled" icon="actions_plus" size={"42px"} emphasize onClick={handleClickCreateNewWorkgroup} text={"Opret ny gruppe"} />
                } />

        <SingleColumnLayout narrow>
            <>
                <OrgChart
                    allWorkgroups={state.workgroupList}
                    organizationOwner={state.organizationOwner}
                    onClickCreateNew={handleClickCreateNewWorkgroup}
                    onClickEditGroup={handleClickEditWorkgroup}
                    onClickEditRootGroup={handleClickEditOwnerGroup}
                />

                <Modal 
                    title={"Organisationsansvarlig"}
                    content={
                        state.inEditOrganizationOwner !== null &&
                        <OrgOwnerEditor
                            organizationOwner={state.inEditOrganizationOwner}
                            onOwnerUpdated={() => handleOrganizationOwnerUpdated(true)}
                            onClose={handleOrganizationOwnerEditorClose} />
                    }
                    showModal={state.inEditOrganizationOwner !== null}
                    onCancel={handleOrganizationOwnerEditorClose}/>
                    

                    {state.inEditWorkgroup &&

                        <Modal 
                            title={"Rediger gruppe"}
                            content={
                                <WorkgroupEditor
                                    allUsers={state.allUsers}
                                    allWorkgroups={state.workgroupList}
                                    inEditWorkgroup={state.inEditWorkgroup}
                                    onUpdated={() => handleWorkgroupUpdated(false)}
                                    onDeleted={() => handleWorkgroupUpdated(true)}
                                    onClose={() => handleWorkgroupEditorClose()} />
                            }
                            showModal={editorOpen}
                            onCancel={() => handleWorkgroupEditorClose()}/>

                    }
            </>
        </SingleColumnLayout>
        </>
    );
}

